// src/pages/PicoBoxPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import { useLocale } from "../contexts/LocaleContext";
import { getAllProductData } from "../api/apiService";
import DownloadsSection from "../components/products/DownloadsSection";
import PicoBoxFeatures from "../components/products/picobox/PicoBoxFeatures";
import BackToProductButton from "../components/products/BackToProductButton";
import ProductThemeShell from "../components/products/ProductThemeShell";
import { flattenStrapiEntity } from "../utils/strapiFlatten";

const PicoBoxPage = () => {
  const { locale } = useLocale();
  const location = useLocation();
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [picoboxItem, setPicoboxItem] = useState(null);

  // Safely render text fields that might come from rich text / object structures
  const safeText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map((v) => safeText(v)).join(" ");
    }
    if (typeof value === "object") {
      if (Array.isArray(value.children)) {
        return value.children.map((child) => safeText(child)).join(" ");
      }
      if (typeof value.text === "string") return value.text;
    }
    return "";
  };

  useEffect(() => {
    const fetchProductContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllProductData(locale);
        setProductData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setProductData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductContent();
  }, [locale]);

  // Find the matching picobox item by URL
  useEffect(() => {
    if (!productData || !productData.cas_picobox_items) return;

    const pathname = location.pathname.toLowerCase();
    const matchingItem = productData.cas_picobox_items.find(
      (item) => {
        const flat = flattenStrapiEntity(item) || item;
        const itemUrl =
          typeof flat.url === "string" ? flat.url.toLowerCase() : "";
        return itemUrl === pathname;
      }
    );
    setPicoboxItem(
      matchingItem ? flattenStrapiEntity(matchingItem) : null
    );
  }, [productData, location.pathname]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  if (!picoboxItem) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
        <BackToProductButton />
      </div>
    );
  }

  // Extract banner data from productData (not from picoboxItem)
  const banner = productData?.banner;
  const bannerTitle = safeText(picoboxItem.name) || "PicoBox";
  const bannerImageUrl = banner?.image?.[0]?.url || banner?.url;
  const bannerTitleIsWhite = banner?.titleColorIsWhite ?? true;

  // Extract features data
  const featuresTitle = safeText(picoboxItem.features);
  const featuresContent = picoboxItem.features_content;
  const featuresImage = picoboxItem.features_image;
  const featuresImageUrl = featuresImage[0]?.image?.url;
  const featuresImageSubtitle = featuresImage[0]?.subtitle;

  // Extract downloads data
  const downloadsTitle = safeText(picoboxItem.downloads);
  const downloadsFiles = picoboxItem.downloads_file || [];

  return (
    <ProductThemeShell>
    <div className="picobox-page min-h-screen">
      {/* Banner */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={bannerTitleIsWhite}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-80 lg:h-96"
      />

      {/* Main Content */}
      <section className="product-section-surface w-full py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Features Section */}
        {featuresTitle && (
          <PicoBoxFeatures
            title={featuresTitle}
            content={featuresContent}
            imageUrl={featuresImageUrl}
            imageSubtitle={featuresImageSubtitle}
            safeText={safeText}
          />
        )}

        {/* Downloads Section */}
        {downloadsTitle && (
          <DownloadsSection
            title={downloadsTitle}
            files={downloadsFiles}
            safeText={safeText}
          />
        )}

        <BackToProductButton />
        </div>
      </section>
    </div>
    </ProductThemeShell>
  );
};

export default PicoBoxPage;

